import { useTranslation } from 'i18n'
import type { History } from 'history'
import React, { useRef, useState } from 'react'
import {
  AccessCodeButton,
  AccessCodeWrapper,
  BlurWrapper,
  ContentWrapper,
  ForgotPassWrapper,
  MyAccessCodeWrapper,
  ToggleBlurIcon,
  TokenWrapper
} from './AccessCodeViewer.styles'

import { colors } from 'config/ui'
import { Button, tokens } from '@arvoreeducacao/bonsai-ds'
import { RESET_PASSWORD_PATH } from 'routes/paths'
import copyToClipboard from 'utils/copyToClipboard'
import type { User } from 'api/data.types'
import { Cdn } from 'utils/assets'
import { SpecialEntities } from 'utils/user'
import { SEC_CULTURA_SP_RESET_PASSWORD_URL } from 'config/vars'

type AccessCodeViewerProps = {
  history: History
  user: User
}

const AccessCodeViewer: React.FC<AccessCodeViewerProps> = ({
  history,
  user
}) => {
  const image = useRef()
  const { t } = useTranslation()
  const [isTokenBlured, setIsTokenBlured] = useState<boolean>(true)
  const [copyToClipboardLabel, setCopyToClipboardLabel] = useState(
    t('Copiar código')
  )

  const copyToClipboardHandler = () => {
    copyToClipboard(user.accessToken)
    setCopyToClipboardLabel(t('Copiado!'))
    setTimeout(() => setCopyToClipboardLabel(t('Copiar código')), 3000)
  }

  const save = () => {
    const myCanvas: HTMLCanvasElement = document.querySelector('#myCanvas')

    const ctx = myCanvas.getContext('2d')

    ctx.clearRect(0, 0, myCanvas.width, myCanvas.height)
    ctx.drawImage(image.current, 0, 0)

    ctx.font = 'bold 20px Averta'
    ctx.fillStyle = tokens.colors.dark6
    ctx.textAlign = 'left'
    ctx.fillText(user.name, myCanvas.width / 20, 140)

    ctx.font = 'normal 14px Averta'
    ctx.fillStyle = tokens.colors.dark6
    ctx.textAlign = 'left'
    ctx.fillText(user.reader.root.name, myCanvas.width / 20, 160)

    ctx.font = 'bold 60px Averta'
    ctx.fillStyle = tokens.colors.dark6
    ctx.textAlign = 'center'
    ctx.fillText(user.accessToken, myCanvas.width / 2, 320)

    const a = document.createElement('a')
    document.body.appendChild(a)
    a.href = myCanvas.toDataURL()
    a.download = 'carteirinha-arvore.png'

    a.click()

    document.body.removeChild(a)
  }

  const handleChangePassword = () => {
    if (SpecialEntities.isSecretariaSp(user)) {
      window.location.href = SEC_CULTURA_SP_RESET_PASSWORD_URL
    } else {
      history.push(RESET_PASSWORD_PATH)
    }
  }

  return (
    <>
      <img
        src={Cdn.getImageUrl('wallet.jpg')}
        ref={image}
        style={{ display: 'none' }}
        crossOrigin={'anonymous'}
        alt=""
      />

      <MyAccessCodeWrapper>
        <h4>{t('Meu acesso')}</h4>
        <p>
          {t(
            'Você pode acessar com o seu código de acesso, que substitui email e senha na hora do login. Guarde com carinho!'
          )}
        </p>
      </MyAccessCodeWrapper>
      <ContentWrapper>
        <AccessCodeWrapper>
          <p>{t('Seu código de acesso:')}</p>
          <TokenWrapper>
            <BlurWrapper
              isTokenBlured={isTokenBlured}
              onClick={() => setIsTokenBlured(false)}
            >
              {user.accessToken}
            </BlurWrapper>
            <ToggleBlurIcon
              iconName={isTokenBlured ? 'eye-off' : 'eye'}
              size={24}
              onClick={() => setIsTokenBlured(!isTokenBlured)}
            />
          </TokenWrapper>
          <AccessCodeButton
            theme={{ type: 'primaryLivros', size: 'xLarge' }}
            isLeft
            iconName={['swap-stroke']}
            textColorHover={colors.black5}
            noTextTransform={true}
            text={copyToClipboardLabel}
            onClick={copyToClipboardHandler}
          />
          <Button
            text={t('Baixar carteirinha')}
            theme={{ type: 'ghost', size: 'xLarge' }}
            fluid
            isLeft
            iconName={['download']}
            onClick={save}
          />
        </AccessCodeWrapper>
        <ForgotPassWrapper>
          <p>{t('Caso acesse com e-mail e senha')}</p>
          <Button
            text={t('Altere sua senha')}
            fluid={false}
            theme={{ type: 'secondary', size: 'large' }}
            onClick={handleChangePassword}
            data-test="change-password-link"
          />
        </ForgotPassWrapper>
      </ContentWrapper>
    </>
  )
}

export default AccessCodeViewer
